<template>
  <a-drawer
    :title="readonly ? '支付宝基础信息' : '支付宝编辑信息'"
    width="600"
    :visible="visible"
    :closable="false"
    :footer-style="{ textAlign: 'right' }"
    @close="touchCancel"
  >
    <a-spin :spinning="isDetailLoading">
      <a-form
        ref="RefForm"
        :model="formState"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <!-- 标签 -->
        <a-form-item label="标签" name="zfb_tag" :rules="readonly ? [] : [{ required: true, message: '请选择标签' }]">
          <a-select
            v-model:value="formState.zfb_tag"
            mode="multiple"
            placeholder="请选择"
            allow-clear
            :disabled="readonly"
            :getPopupContainer="(t) => t.parentNode"
          >
            <a-select-option v-for="item in ZfbTagList" :key="item" :value="item">{{ item }}</a-select-option>
          </a-select>
        </a-form-item>
        <!-- 全网首轮播出 -->
        <a-form-item label="全网首轮播出" name="zfb_first_play" :rules="readonly ? [] : [{ required: true, message: '请选择是否全网首轮播出' }]">
          <a-radio-group v-model:value="formState.zfb_first_play" :disabled="readonly">
            <a-radio :value="1">是</a-radio>
            <a-radio :value="0">否</a-radio>
          </a-radio-group>
        </a-form-item>
        <!-- 制作成本 -->
        <a-form-item label="制作成本" name="cost_of_production" :rules="readonly ? [] : [{ required: true, message: '请输入制作成本' }]">
          <a-input
            v-model:value="formState.cost_of_production"
            placeholder="请输入"
            suffix="万元"
            :disabled="readonly"
          />
        </a-form-item>
        <!-- 承诺函及作品清单 -->
        <a-form-item label="承诺函及作品清单" name="zfb_authorized_material" :rules="readonly ? [] : [{ required: true, validator: validatorMaterial }]">
          <a-form-item-rest>
            <Upload
              v-if="!readonly"
              accept=".pdf,.jpg,.jpeg,.png"
              uploadSource="ZfbCopyright"
              :multiple="true"
              :uploadResult="(fileJson) => uploadResult(fileJson)"
              :fileList="formState.zfb_authorized_material"
              :uploadBucket="$pub.BUCKET_PUB()"
            />
          </a-form-item-rest>
          <div v-if="!readonly" style="color: #8c8c8c; font-size: 12px; margin-top: 4px;">确保上传的资料完整性，支持 pdf、jpg 等格式，文件大小请控制在 100MB 以内</div>
          <div v-if="formState.zfb_authorized_material.length" style="margin-top: 8px;">
            <div
              v-for="(item, index) in formState.zfb_authorized_material"
              :key="item.uid || index"
              style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px; font-size: 13px;"
            >
              <PaperClipOutlined style="color: #8c8c8c;" />
              <a
                v-if="item.url"
                :href="item.url"
                target="_blank"
                style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
              >{{ item.name || item.url }}</a>
              <span v-else style="flex: 1; color: #8c8c8c;">{{ item.name }}（上传中...）</span>
              <a v-if="!readonly" style="color: #ff4d4f; flex-shrink: 0;" @click="touchDeleteFile(index)">删除</a>
            </div>
          </div>
          <div v-if="readonly && !formState.zfb_authorized_material.length" style="color: #bfbfbf; font-size: 13px;">暂无文件</div>
        </a-form-item>
      </a-form>
    </a-spin>
    <!-- footer -->
    <template #footer>
      <template v-if="readonly">
        <a-button type="primary" @click="touchCancel">关闭</a-button>
      </template>
      <template v-else>
        <a-button style="margin-right: 8px" @click="touchCancel">取消</a-button>
        <a-button type="primary" :loading="isLoading" @click="touchOk">保存</a-button>
      </template>
    </template>
  </a-drawer>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { message } from 'ant-design-vue'
import { PaperClipOutlined } from '@ant-design/icons-vue'
import Upload from '@/components/Upload'
import { thirdDramaDetail, zfbSave } from '@/api/copyright'
import { ZfbTagList } from '@/utils/constantFilmLibrary'

const emit = defineEmits(['success'])

// ========================= Refs =========================
const RefForm = ref(null)
const visible = ref(false)
const isLoading = ref(false)
const isDetailLoading = ref(false)
const readonly = ref(false)

// 当前记录
let currentRecord = null

// 表单
const formState = reactive({
  zfb_tag: [],
  zfb_first_play: undefined,
  cost_of_production: undefined,
  zfb_authorized_material: []
})

// ========================= 工具函数 =========================
function urlsToFileList(urls) {
  if (!urls || !Array.isArray(urls)) return []
  return urls.filter(Boolean).map((url, i) => ({
    uid: `saved-${i}-${url}`,
    name: url.split('/').pop() || `file-${i}`,
    status: 'done',
    url
  }))
}

function resetForm() {
  RefForm.value?.resetFields()
  formState.zfb_tag = []
  formState.zfb_first_play = undefined
  formState.cost_of_production = undefined
  formState.zfb_authorized_material = []
}

function fillForm(record) {
  formState.zfb_tag = Array.isArray(record.zfb_tag) ? record.zfb_tag : []
  formState.zfb_first_play = record.zfb_first_play !== undefined && record.zfb_first_play !== null ? record.zfb_first_play : undefined
  formState.cost_of_production = record.cost_of_production !== undefined && record.cost_of_production !== null ? record.cost_of_production : undefined
  formState.zfb_authorized_material = urlsToFileList(record.zfb_authorized_material)
}

// ========================= 暴露方法 =========================
function show(record, isReadonly = false) {
  currentRecord = record
  readonly.value = isReadonly
  resetForm()
  visible.value = true
  getDetail(record.id)
}

defineExpose({ show })

// ========================= 事件处理 =========================
function touchOk() {
  RefForm.value?.validate().then(() => {
    const uploading = formState.zfb_authorized_material.some(f => f.status === 'uploading')
    if (uploading) {
      message.warning('文件上传中，请稍候再提交')
      return
    }
    submitSave()
  }).catch(() => {})
}

function touchCancel() {
  visible.value = false
}

function touchDeleteFile(index) {
  formState.zfb_authorized_material.splice(index, 1)
  RefForm.value?.validateFields(['zfb_authorized_material']).catch(() => {})
}

function validatorMaterial(_, value) {
  const list = formState.zfb_authorized_material.filter(f => f.status === 'done' && f.url)
  if (!list.length) {
    return Promise.reject('请上传承诺函及作品清单')
  }
  return Promise.resolve()
}

function uploadResult(fileJson) {
  // Upload 组件上传完成后自动更新 fileList
}

// ========================= 接口 =========================
function getDetail(id) {
  isDetailLoading.value = true
  thirdDramaDetail({ id, type: 'zfb' }).then(res => {
    isDetailLoading.value = false
    const { code, data } = res
    if (code === 0 && data) {
      fillForm(data)
    } else {
      message.error(res.msg || res.message || '获取详情失败')
    }
  }).catch(() => {
    isDetailLoading.value = false
    message.error('获取详情失败')
  })
}

function submitSave() {
  isLoading.value = true
  const params = {
    drama_id: currentRecord?.id,
    zfb_tag: formState.zfb_tag,
    zfb_first_play: formState.zfb_first_play,
    cost_of_production: formState.cost_of_production,
    zfb_authorized_material: formState.zfb_authorized_material
      .filter(f => f.status === 'done' && f.url)
      .map(f => f.url)
  }
  zfbSave(params).then(res => {
    isLoading.value = false
    if (res.code === 0) {
      message.success('保存成功')
      visible.value = false
      emit('success')
    } else {
      message.error(res.msg || res.message || '保存失败')
    }
  }).catch(() => {
    isLoading.value = false
    message.error('保存失败')
  })
}
</script>
